import React from "react";
import { Search, Filter, X } from "lucide-react";
import type { SearchFilters } from "@/types";
import { mockTags } from "@/lib/mock-data";
import { useAnalytics } from "@/hooks/useAnalytics";

interface SearchBarProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  placeholder?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  filters,
  onFiltersChange,
  placeholder = "Search agent titles and descriptions...",
}) => {
  const [showFilters, setShowFilters] = React.useState(false);
  const { trackSearch, trackButtonClick } = useAnalytics();

  const handleQueryChange = (query: string) => {
    onFiltersChange({ ...filters, query });

    // Track search events (debounced in production)
    if (query.length >= 3) {
      trackSearch(query);
    }
  };

  const handleTagToggle = (tag: string) => {
    const newTags = filters.tags.includes(tag)
      ? filters.tags.filter((t) => t !== tag)
      : [...filters.tags, tag];
    onFiltersChange({ ...filters, tags: newTags });

    // Track filter usage
    trackButtonClick(
      `tag-${tag}`,
      filters.tags.includes(tag) ? "remove-filter" : "add-filter",
    );
  };

  const handleSortChange = (sortBy: SearchFilters["sortBy"]) => {
    onFiltersChange({ ...filters, sortBy });
  };

  const clearFilters = () => {
    onFiltersChange({ query: "", tags: [], sortBy: "newest" });
  };

  return (
    <div className="w-full max-w-2xl space-y-4">
      {/* Main Search Input */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500 dark:text-slate-400" />
        <input
          type="text"
          placeholder={placeholder}
          value={filters.query}
          onChange={(e) => handleQueryChange(e.target.value)}
          className="flex h-14 w-full rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-12 py-4 text-base text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:border-orange-500 shadow-lg transition-all duration-200"
        />
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-2 rounded-lg bg-slate-100 dark:bg-slate-700 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors hover:bg-slate-200 dark:hover:bg-slate-600"
        >
          <Filter className="h-4 w-4" />
          Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Filters
            </h3>
            <button
              onClick={clearFilters}
              className="text-sm text-slate-600 dark:text-slate-400 hover:text-orange-500 dark:hover:text-orange-400"
            >
              Clear All
            </button>
          </div>

          {/* Sort Options */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Sort by
            </label>
            <select
              value={filters.sortBy}
              onChange={(e) =>
                handleSortChange(e.target.value as SearchFilters["sortBy"])
              }
              className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="newest">Newest First</option>
              <option value="popular">Most Popular</option>
              <option value="trending">Trending</option>
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2">
              {mockTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagToggle(tag)}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                    filters.tags.includes(tag)
                      ? "bg-orange-500 text-white"
                      : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                  }`}
                >
                  {tag}
                  {filters.tags.includes(tag) && <X className="h-3 w-3" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
